import React, { useState, useEffect, useMemo } from 'react';
import BBALoader from '../components/BBALoader';
import { useNavigate } from 'react-router-dom';
import PortalBackground from '../components/PortalBackground';
import BreakthroughSessionBackground, { getBreakthroughTheme } from '../components/portal/BreakthroughSessionBackground';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, orderBy, getDocs, addDoc, Timestamp, doc, getDoc } from 'firebase/firestore';
import { getAccounts, getCrmContactDisplayPhotoUrl, getCrmCompanyLogoDisplayUrl, getCrmTeamMemberDisplayPhotoUrl } from '../services/firestoreService';
import { normalizeVisitStatus, parseDateKey, formatWindowRange } from '../services/visitSchedulingService';
import { getInternalUsers } from '../services/userService';
import { getTasksByUser } from '../services/taskService';
import { isKickoffEligibleClient } from '../utils/crmClientListFilters';
import {
  computeStrategyCallUsage,
  findStrategyCallDeliverableContext,
  getClientActualStartDate,
  resolveDeliverableProgress,
  parseFlexibleAccountDate,
} from '../utils/accountProgramDeliverables';
import { checkInDateToJs } from '../components/crm/CheckInInsights';
import { fetchGmailMessagesForEmailWithStatus } from '../services/gmailService';
import { fetchGoogleCalendarEvents } from '../services/googleCalendarService';
import {
  fetchPortalUsersLinkedToAccounts,
  fetchClientEventRosterMembers,
} from '../services/accountTeamMembersService';
import {
  buildClientEventRoster,
  eventMatchesClientRoster,
  calendarEventDedupeKey,
  resolveCalendarEventHost,
} from '../utils/clientCalendarEventMatching';
import { db } from '../firebase';
import { getMarketingData } from '../services/marketingDataService';
import { isActiveClientAccount, resolveClientLifecycle } from '../utils/accountLifecycle'; // one client-lifecycle rule

/**
 * Match GeneralFulfillment visit enrichment: Leads → Clients by email, then users/{userId}.
 * Returns the company name + CRM company logo, plus the visiting contact's name and profile
 * picture, so meeting mode can show both the company and who's coming.
 */
async function resolveVisitCompanyInfo(data) {
  const explicit = data.companyName && String(data.companyName).trim();
  let company = explicit || '';
  let logo = '';
  let contactName = (data.requestedByName && String(data.requestedByName).trim()) || '';
  let contactPhoto = '';

  const emailCandidates = [];
  if (data.userEmail) {
    const e = String(data.userEmail).trim();
    emailCandidates.push(e);
    const lower = e.toLowerCase();
    if (lower !== e) emailCandidates.push(lower);
  }

  for (const email of emailCandidates) {
    try {
      let accountsSnapshot = await getDocs(
        query(
          collection(db, 'accounts'),
          where('contact.email', '==', email),
          where('type', '==', 'Leads')
        )
      );
      if (accountsSnapshot.empty) {
        accountsSnapshot = await getDocs(
          query(
            collection(db, 'accounts'),
            where('contact.email', '==', email),
            where('type', '==', 'Clients')
          )
        );
      }
      if (!accountsSnapshot.empty) {
        const accId = accountsSnapshot.docs[0].id;
        const accData = { id: accId, ...accountsSnapshot.docs[0].data() };
        if (!company) company = accData.company || '';
        logo = getCrmCompanyLogoDisplayUrl(accData) || logo;
        // Contact picture the same way Client Overview resolves it: the CRM team-member
        // photo override for the visiting portal user wins, then the account contact photo.
        const teamPhoto = data.userId ? getCrmTeamMemberDisplayPhotoUrl(accData, data.userId) : '';
        contactPhoto = teamPhoto || getCrmContactDisplayPhotoUrl(accData) || contactPhoto;
        if (!contactName) contactName = contactDisplayNameFromAccount(accData);
        break;
      }
    } catch (error) {
      console.error('Error fetching account for visit company:', error);
    }
  }

  // Portal user fallback fills in anything the account lookup didn't provide.
  if ((!contactPhoto || !company || !contactName || !logo) && data.userId) {
    try {
      const userDoc = await getDoc(doc(db, 'users', data.userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (!contactPhoto) contactPhoto = pickProfilePicUrl(userData) || '';
        if (!logo) logo = userData.companyLogo || '';
        if (!company && userData.company && userData.company !== 'Unknown Company') {
          company = userData.company;
        }
        if (!contactName) contactName = displayNameFromPortalUserData(userData);
      }
    } catch (error) {
      console.error('Error fetching user for visit company info:', error);
    }
  }

  return { company: company || 'Unknown Company', logo, contactName, contactPhoto };
}

function displayNameFromPortalUserData(data) {
  if (!data) return '';
  const full = `${data.firstName || ''} ${data.lastName || ''}`.trim();
  if (full) return full;
  const dn = data.displayName || data.name;
  if (dn && String(dn).trim()) return String(dn).trim();
  return '';
}

function contactDisplayNameFromAccount(account) {
  if (!account) return '';
  const c = account.contact || {};
  const full = `${c.firstName || ''} ${c.lastName || ''}`.trim();
  if (full) return full;
  if (account.company && String(account.company).trim()) return String(account.company).trim();
  return '';
}

/** Company initials fallback when no logo is on file. */
function visitCompanyInitials(name) {
  const w = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!w.length) return '?';
  if (w.length === 1) return w[0].slice(0, 2).toUpperCase();
  return (w[0][0] + w[w.length - 1][0]).toUpperCase();
}

/**
 * Client Visits card body — visits grouped by date. Companies sharing a date sit under a
 * single date heading. A shared window holds up to two companies, so each company fills half
 * the row; a lone company fills the whole width. Every company shows its logo + name and the
 * visiting contact's profile picture + name.
 */
function VisitGroups({ groups }) {
  if (!groups || groups.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
        No upcoming visits scheduled
      </p>
    );
  }
  return (
    <div className="space-y-5">
      {groups.map((g) => (
        <div key={g.key}>
          <div className="flex items-center justify-center gap-2 mb-3">
            {(g.types || []).map((t) => (
              <span
                key={t}
                className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                  t === 'CQC'
                    ? 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200'
                    : 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                }`}
              >
                {t}
              </span>
            ))}
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-200 text-center">
              {g.dateLabel}
            </span>
            {g.isWithin7Days && <span aria-hidden="true">⭐</span>}
          </div>
          <div className={`grid gap-4 ${g.companies.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
            {g.companies.map((c) => (
              <div
                key={c.id}
                className="flex flex-col items-center text-center rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 p-3 gap-2"
              >
                {/* Company logo + name */}
                <div className="w-14 h-14 rounded-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-600 flex items-center justify-center overflow-hidden">
                  {c.logo ? (
                    <img
                      src={c.logo}
                      alt={c.company}
                      className="max-w-[85%] max-h-[85%] object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div
                    className="w-full h-full items-center justify-center text-sm font-semibold text-gray-500 dark:text-gray-300"
                    style={{ display: c.logo ? 'none' : 'flex' }}
                  >
                    {visitCompanyInitials(c.company)}
                  </div>
                </div>
                <span className="text-sm font-semibold text-gray-800 dark:text-gray-100 break-words leading-tight w-full">
                  {c.company}
                </span>

                {/* Visiting contact: profile picture + name */}
                {(c.contactPhoto || c.contactName) && (
                  <div className="flex items-center gap-2 max-w-full">
                    <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-600 flex items-center justify-center flex-shrink-0">
                      {c.contactPhoto ? (
                        <img
                          src={c.contactPhoto}
                          alt={c.contactName || 'Contact'}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div
                        className="w-full h-full items-center justify-center text-[10px] font-semibold text-gray-600 dark:text-gray-200"
                        style={{ display: c.contactPhoto ? 'none' : 'flex' }}
                      >
                        {visitCompanyInitials(c.contactName)}
                      </div>
                    </div>
                    {c.contactName && (
                      <span className="text-xs text-gray-600 dark:text-gray-300 break-words leading-tight text-left">
                        {c.contactName}
                      </span>
                    )}
                  </div>
                )}

                <span
                  className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${
                    c.status === 'Met with'
                      ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                      : 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                  }`}
                >
                  {c.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

const pickProfilePicUrl = (data) => {
  if (!data) return '';
  const candidates = [
    data.profileImage,
    data.profilePicture,
    data.photoURL,
    data.photoUrl,
    data.avatarUrl,
    data.avatarURL,
    data.imageUrl,
    data.imageURL,
    data.picture,
    data.profilePic,
  ];
  const url = candidates.find((v) => typeof v === 'string' && v.trim().length > 0);
  return url ? url.trim() : '';
};

/** Matches ClientOverview's per-account pinned-contact localStorage key. */
const PINNED_CONTACT_STORAGE_PREFIX = 'clientOverview:pinnedContactKey:';

/** Program doc id keyed by program status (mirrors useStrategyCalls). */
const PROGRAM_STATUS_MAP = {
  Lite: 'Lite',
  Plus: 'Plus',
  Pro: 'Pro',
  Hourly: 'Hourly',
  '5 Hours': '5 Hours',
  '10 Hours': '10 Hours',
  'A La Carte': 'A La Carte',
};

/** Emoji for a roadmap step's department/type. */
const departmentEmoji = (dept) => {
  const d = (dept || '').toLowerCase();
  if (d.includes('sales')) return '💰';
  if (d.includes('operation')) return '⚙️';
  if (d.includes('finance')) return '📊';
  if (d.includes('tech')) return '💻';
  if (d.includes('marketing')) return '📣';
  if (d.includes('hr') || d.includes('human')) return '👥';
  if (d.includes('onboard')) return '🚀';
  if (d.includes('design')) return '🎨';
  return '📋';
};

const MeetingMode = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
    const { currentUser } = useAuth();
  const [currentSection, setCurrentSection] = useState(0);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [clientVisits, setClientVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  const [allAccounts, setAllAccounts] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [comments, setComments] = useState([]);
  const [newQuestion, setNewQuestion] = useState('');
  const [newComment, setNewComment] = useState('');
  const [leadCards, setLeadCards] = useState([]);
  const [internalUsers, setInternalUsers] = useState([]);
  const [tasksByUser, setTasksByUser] = useState({});
  const [taskPageByUser, setTaskPageByUser] = useState({});
  const [taskPageDropdownOpen, setTaskPageDropdownOpen] = useState(null); // userId or null
  const [marketingData, setMarketingData] = useState(null);
  const [marketingLoading, setMarketingLoading] = useState(false);
  const [breakthroughSessions, setBreakthroughSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [clientActivity, setClientActivity] = useState({ rows: [], totals: { total: 0, used: 0, remaining: 0 } });
  const [clientActivityLoading, setClientActivityLoading] = useState(false);
  // Last email exchanged with each client, read from the signed-in user's Gmail.
  const [lastEmailByAccount, setLastEmailByAccount] = useState({});
  const [lastEmailStatus, setLastEmailStatus] = useState('idle');

  // Helper functions for breakthrough sessions
  const dateToISO = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatShortDate = (date) =>
    date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

  // Current calendar month, used to label the pooled call totals and check-in chips.
  const monthLabel = new Date().toLocaleDateString(undefined, { month: 'long' });

  const initialsFor = (presenter, fallback) => {
    if (!presenter) return fallback;
    return presenter
      .split(' ')
      .map((n) => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
  };

  const sessionFromDoc = (docSnap) => {
    const data = docSnap.data();
    const dateObj = data.date?.toDate ? data.date.toDate() : new Date(data.date);
    return {
      id: docSnap.id,
      date: dateObj,
      isoDate: dateToISO(dateObj),
      eventName: data.eventName || 'Breakthrough Session',
      presenter: data.presenter || '',
      presenterId: data.presenterId || '',
      presenterPhoto: data.presenterPhoto || '',
      presenterInitials: data.presenterInitials || '',
      status: data.status || 'active',
      department: data.department || 'General',
      description: data.description || '',
      startTime: data.startTime || '',
      endTime: data.endTime || '',
      needPresenter: data.needPresenter || false,
      label: dateObj.toLocaleDateString(undefined, { weekday: 'long' }),
    };
  };


  const formatTaskDueDate = (value) => {
    if (!value) return 'No date';
    let date = null;
    try {
      if (value && typeof value.toDate === 'function') {
        date = value.toDate();
      } else if (value instanceof Date) {
        date = value;
      } else if (typeof value === 'number') {
        date = new Date(value);
      } else if (typeof value === 'string') {
        const parsed = new Date(value);
        if (!isNaN(parsed.getTime())) date = parsed;
      } else if (value && typeof value.seconds === 'number') {
        date = new Date(value.seconds * 1000);
      }
    } catch (e) {}
    return date && !isNaN(date.getTime()) ? date.toLocaleDateString() : 'No date';
  };

  const getDueDateObj = (value) => {
    if (!value) return null;
    try {
      if (value && typeof value.toDate === 'function') return value.toDate();
      if (value instanceof Date) return value;
      if (typeof value === 'number') return new Date(value);
      if (typeof value === 'string') {
        const parsed = new Date(value);
        return isNaN(parsed.getTime()) ? null : parsed;
      }
      if (value && typeof value.seconds === 'number') return new Date(value.seconds * 1000);
    } catch (e) { return null; }
    return null;
  };

  const isOverdue = (value) => {
    const d = getDueDateObj(value);
    if (!d) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d < today;
  };

  const clientProgramColors = {
    'Lite': '#3B82F6',
    'Plus': '#8B5CF6',
    'Pro': '#10B981',
    'Hourly': '#F59E0B',
    '5 Hours': '#EC4899',
    '10 Hours': '#06B6D4',
    'A La Carte': '#8B5CF6'
  };

  const toDateSafe = (value) => {
    try {
      if (!value) return null;
      if (value && typeof value.toDate === 'function') return value.toDate();
      if (value instanceof Date) return value;
      if (typeof value === 'number') return new Date(value);
      if (typeof value === 'string') {
        const d = new Date(value);
        return isNaN(d.getTime()) ? null : d;
      }
      if (value && typeof value.seconds === 'number') return new Date(value.seconds * 1000);
      return null;
    } catch {
      return null;
    }
  };

  // Fetch events from Firebase
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch all conferences
        try {
          const conferencesRef = collection(db, 'conferences');
          const conferencesQuery = query(
            conferencesRef,
            orderBy('startDate', 'asc')
          );
          
          const conferencesSnapshot = await getDocs(conferencesQuery);
          const conferencesData = conferencesSnapshot.docs.map(doc => {
            const conferenceData = doc.data();
            return {
              id: doc.id,
              title: conferenceData.name,
              description: conferenceData.notes || '',
              startTime: conferenceData.startDate?.toDate() || null,
              endTime: conferenceData.endDate?.toDate() || null,
              location: conferenceData.location || '',
              type: conferenceData.type || 'Attendee',
              ...conferenceData
            };
          });
          
          console.log('Conferences fetched:', conferencesData); // Debug log
          setUpcomingEvents(conferencesData);
        } catch (conferencesError) {
          console.error('Error fetching conferences:', conferencesError);
          setUpcomingEvents([]);
        }
        
        // Fetch client visits from general fulfillment.
        // The current visit-scheduling model stores day1/day2 (YYYY-MM-DD) + a
        // requested→scheduled→completed status (see visitSchedulingService). Meeting mode
        // shows the confirmed ones: "Scheduled" (upcoming/in-progress) and "Met with"
        // (recently completed), grouped by date with each company's logo.
        try {
          const now = new Date();
          const pad = (n) => String(n).padStart(2, '0');
          const dateKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
          const todayKey = dateKey(now);
          const past30Key = dateKey(new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000));

          const mapVisitDoc = async (docSnap, visitType) => {
            const data = docSnap.data();
            const status = normalizeVisitStatus(data.status);
            // Only confirmed visits: scheduled (upcoming) or completed (met with).
            if (status !== 'scheduled' && status !== 'completed') return null;

            const day1 = data.day1 || '';
            const day2 = data.day2 || day1;
            if (!day1) return null;
            const endDay = day2 || day1;

            // Scheduled: keep while the window hasn't fully passed.
            // Completed ("met with"): keep if it happened within the last 30 days.
            if (status === 'scheduled' && endDay < todayKey) return null;
            if (status === 'completed' && day1 < past30Key) return null;

            const { company, logo, contactName, contactPhoto } = await resolveVisitCompanyInfo(data);
            const visitDate = parseDateKey(day1);
            const daysUntilVisit = visitDate
              ? Math.ceil((visitDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
              : null;

            return {
              id: docSnap.id,
              company,
              logo,
              contactName,
              contactPhoto,
              visitType,
              status: status === 'completed' ? 'Met with' : 'Scheduled',
              rawStatus: status,
              day1,
              day2,
              dayKey: `${day1}|${day2}`,
              dateLabel: formatWindowRange(day1, day2),
              visitDate,
              text: `${company} — ${visitType}`,
              isWithin7Days: status === 'scheduled' && daysUntilVisit !== null && daysUntilVisit <= 7 && daysUntilVisit >= 0,
              isVisit: true,
            };
          };

          const onsiteSnapshot = await getDocs(query(collection(db, 'onSiteRequests'), orderBy('createdAt', 'desc')));
          const cqcSnapshot = await getDocs(query(collection(db, 'cqcVisitRequests'), orderBy('createdAt', 'desc')));

          const onsiteVisits = await Promise.all(onsiteSnapshot.docs.map((d) => mapVisitDoc(d, 'On-Site')));
          const cqcVisits = await Promise.all(cqcSnapshot.docs.map((d) => mapVisitDoc(d, 'CQC')));

          const allVisits = [...onsiteVisits, ...cqcVisits]
            .filter(Boolean)
            // Upcoming scheduled first (ascending), then recently met with.
            .sort((a, b) => {
              if (a.rawStatus !== b.rawStatus) return a.rawStatus === 'scheduled' ? -1 : 1;
              return String(a.day1).localeCompare(String(b.day1));
            });

          console.log('Client visits fetched:', allVisits);
          setClientVisits(allVisits);
        } catch (visitsError) {
          console.error('Error fetching client visits:', visitsError);
          setClientVisits([]);
        }

        // Fetch client accounts for modal selection
        try {
          const accountsData = await getAccounts();
          setAllAccounts(accountsData || []);

          // Build lead cards from real accounts (Leads type) while keeping existing layout
          try {
            const leads = (accountsData || [])
              .filter(a => {
                // Must be a lead type
                if (!a || (a.type !== 'Leads' && a.type !== 'leads')) {
                  return false;
                }
                // Filter out leads where showOnKanban is false (default to true if not set for backward compatibility)
                if (a.showOnKanban === false) {
                  return false;
                }
                return true;
              })
              .map(a => {
                // Use receivedDate if available, otherwise fall back to createdAt
                const received = toDateSafe(a.receivedDate) || toDateSafe(a.createdAt || a.created_at || a.lastActivityAt || a.lastActivity_at) || new Date();
                const updated = toDateSafe(a.updatedAt || a.updated_at || a.lastActivityAt || a.lastActivity_at) || received;
                const contactFirst = a.contact?.firstName || '';
                const contactLast = a.contact?.lastName || '';
                const contactName = `${contactFirst} ${contactLast}`.trim();
                const isLost = a.stage === 'Lost';
                return {
                  id: a.id || `${a.company || 'lead'}-${received.getTime()}`,
                  name: a.company || contactName || 'Unknown',
                  contactName,
                  status: isLost ? 'Lost' : (a.substatus || a.stage || 'New'),
                  stage: a.stage || 'Lead Received',
                  source: a.leadSource || 'Website',
                  receivedDate: received,
                  createdAt: received, // Keep for backward compatibility in calculations
                  notes: a.notes || [], // Include notes for contact calculation
                  accountId: a.id, // Keep reference to account ID for lookup
                  ...(isLost ? { lostInfo: { lostDate: updated, lostStage: a.stage || 'Lost', reason: a.lostReason || a.reason || '' } } : {})
                };
              })
              .sort((a, b) => (b.receivedDate?.getTime?.() || 0) - (a.receivedDate?.getTime?.() || 0));
            setLeadCards(leads);
          } catch (mapErr) {
            console.error('Error mapping leads for MeetingMode:', mapErr);
            setLeadCards([]);
          }
        } catch (accountsError) {
          console.error('Error fetching accounts:', accountsError);
          setAllAccounts([]);
          setLeadCards([]);
        }
        
        // Fetch breakthrough sessions
        try {
          setSessionsLoading(true);
          const now = new Date();
          const startBoundary = Timestamp.fromDate(new Date('2026-01-01'));
          const endBoundary = Timestamp.fromDate(new Date(now.getTime() + 60 * 24 * 60 * 60 * 1000)); // 60 days from now

          const sessionsQuery = query(
            collection(db, 'breakthroughSessions'),
            where('date', '>=', startBoundary),
            where('date', '<=', endBoundary),
            orderBy('date', 'asc')
          );

          const sessionsSnapshot = await getDocs(sessionsQuery);
          const sessions = [];
          
          sessionsSnapshot.forEach((docSnap) => {
            const session = sessionFromDoc(docSnap);
            const sessionDate = new Date(session.date);
            sessionDate.setHours(0, 0, 0, 0);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            // Only include future sessions
            if (sessionDate >= today) {
              sessions.push(session);
            }
          });

          // Sort by date and take the next 8
          sessions.sort((a, b) => a.date - b.date);
          const nextEight = sessions.slice(0, 8);

          // Backfill presenter photos from internal_users when missing
          const sessionsNeedingPhotos = nextEight.filter((s) => s.presenterId && !s.presenterPhoto);
          if (sessionsNeedingPhotos.length > 0) {
            const uniqueIds = Array.from(new Set(sessionsNeedingPhotos.map((s) => s.presenterId)));
            const photoEntries = await Promise.all(
              uniqueIds.map(async (id) => {
                try {
                  const snap = await getDoc(doc(db, 'internal_users', id));
                  if (!snap.exists()) return [id, ''];
                  return [id, pickProfilePicUrl(snap.data())];
                } catch {
                  return [id, ''];
                }
              })
            );
            const photoById = Object.fromEntries(photoEntries);
            setBreakthroughSessions(
              nextEight.map((s) => ({
                ...s,
                presenterPhoto: s.presenterPhoto || photoById[s.presenterId] || '',
              }))
            );
          } else {
            setBreakthroughSessions(nextEight);
          }
        } catch (sessionsError) {
          console.error('Error fetching breakthrough sessions:', sessionsError);
          setBreakthroughSessions([]);
        } finally {
          setSessionsLoading(false);
        }
        
        // Fetch internal users and their tasks (incomplete only) — everyone internal
        try {
          const users = await getInternalUsers();
          setInternalUsers(users);
          const results = await Promise.all(
            users.map(async (u) => {
              try {
                const userTasks = await getTasksByUser(u.id);
                const notDone = (userTasks || []).filter((t) => !t.completed);
                return [u.id, notDone];
              } catch (e) {
                return [u.id, []];
              }
            })
          );
          const map = Object.fromEntries(results);
          setTasksByUser(map);
        } catch (usersErr) {
          console.error('Error fetching internal users/tasks:', usersErr);
          setInternalUsers([]);
          setTasksByUser({});
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setUpcomingEvents([]);
        setClientVisits([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Load marketing data for the Growth slide. Only the raw website + social entries are
  // read (see `growth`), so nothing else is kept in state.
  useEffect(() => {
    const loadMarketingData = async () => {
      if (!currentUser) return;
      try {
        setMarketingLoading(true);
        const data = await getMarketingData();
        setMarketingData({
          websiteEntries: data.websiteEntries,
          socialEntries: data.socialEntries,
        });
      } catch (e) {
        console.error('Error loading marketing data in MeetingMode:', e);
        setMarketingData(null);
      } finally {
        setMarketingLoading(false);
      }
    };

    loadMarketingData();
  }, [currentUser]);

  // Build per-client call activity, next roadmap steps, and pooled 1:1 Strategy Call totals
  useEffect(() => {
    let active = true;
    const loadClientActivity = async () => {
      const clients = (allAccounts || []).filter(isKickoffEligibleClient);
      if (clients.length === 0) {
        setClientActivity({ rows: [], totals: { total: 0, used: 0, remaining: 0 } });
        return;
      }

      setClientActivityLoading(true);
      try {
        const now = new Date();
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        // Calendar month, used for the pooled "this month" totals and the check-in card.
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 1);
        const inThisMonth = (d) => d instanceof Date && !Number.isNaN(d.getTime()) && d >= monthStart && d < monthEnd;

        // Strategy-call allowance needs each program's contractItems + the shared à la carte catalog.
        const alaCarteSnap = await getDocs(collection(db, 'alaCarteItems'));
        const itemsData = alaCarteSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const programCache = {};
        const getContractItems = async (programStatus) => {
          const programId = PROGRAM_STATUS_MAP[programStatus] || programStatus;
          if (programCache[programId]) return programCache[programId];
          try {
            const snap = await getDocs(collection(db, 'programs', programId, 'contractItems'));
            const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            programCache[programId] = items;
            return items;
          } catch (e) {
            programCache[programId] = [];
            return [];
          }
        };

        // Meetings come from Google Calendar — the same pipeline as the ClientOverview
        // Events tab, which is the source of truth for what actually happened (canceled
        // events disappear from the calendar; reschedules move with the event). Fetch
        // each internal user's calendar once for the whole window, then match events
        // per client below.
        const calendarWindowEnd = new Date(now);
        calendarWindowEnd.setMonth(calendarWindowEnd.getMonth() + 6);
        const eventsPerUser = await Promise.all(
          (internalUsers || []).map(async (u) => {
            try {
              return await fetchGoogleCalendarEvents(u.id, thirtyDaysAgo, calendarWindowEnd);
            } catch (e) {
              return [];
            }
          })
        );
        // Dedupe events seen from multiple internal calendars: keep the copy with the
        // fullest attendee list, but remember whose calendar it first came from (the
        // Events tab's host fallback).
        const calendarEventMap = new Map();
        (internalUsers || []).forEach((u, i) => {
          (eventsPerUser[i] || []).forEach((event) => {
            const key = calendarEventDedupeKey(event);
            const existing = calendarEventMap.get(key);
            if (!existing) {
              calendarEventMap.set(key, { event, fetchedBy: u });
            } else if ((event.attendees || []).length > (existing.event.attendees || []).length) {
              calendarEventMap.set(key, { event, fetchedBy: existing.fetchedBy });
            }
          });
        });
        const calendarEvents = Array.from(calendarEventMap.values());

        // Per-client matching roster — the same roster the Events tab builds (portal
        // users + CRM-linked users + pending invites, with alternate emails).
        const rosterByClient = {};
        await Promise.all(
          clients.map(async (c) => {
            try {
              rosterByClient[c.id] = await fetchClientEventRosterMembers(c);
            } catch (e) {
              rosterByClient[c.id] = [];
            }
          })
        );

        // Check-ins for the current calendar month, indexed by accountId and by email so a
        // client whose check-in only carries userEmail still matches.
        const checkInsByAccountId = new Map();
        const checkInsByEmail = new Map();
        try {
          const checkInsSnap = await getDocs(collection(db, 'check_ins'));
          checkInsSnap.forEach((d) => {
            const data = d.data() || {};
            const rating = Number(data.overallSatisfaction ?? data.rating);
            const hasRating = Number.isFinite(rating) && rating >= 1 && rating <= 5;
            const responded =
              hasRating ||
              data.status === 'completed' ||
              (typeof data.overallStatus === 'string' && ['Good', 'Okay', 'Not Good'].includes(data.overallStatus));
            const when = responded
              ? checkInDateToJs(data.submittedAt || data.completedAt || data.createdAt)
              : checkInDateToJs(data.scheduledDate || data.createdAt);
            if (!inThisMonth(when)) return;
            const entry = {
              responded,
              rating: hasRating ? rating : null,
              status: data.overallStatus || (responded ? 'Completed' : 'Awaiting response'),
              comments: String(data.comments || data.additionalFeedback || '').trim(),
              requestedCall: data.requestedCheckInCall === true,
              date: when,
            };
            // Newest wins when a client has more than one this month.
            const keep = (map, key) => {
              if (!key) return;
              const prev = map.get(key);
              if (!prev || (entry.date && prev.date && entry.date > prev.date)) map.set(key, entry);
            };
            keep(checkInsByAccountId, data.accountId);
            keep(checkInsByEmail, String(data.userEmail || '').trim().toLowerCase());
          });
        } catch (e) {
          // A check-in read failure just means the cards show no check-in.
        }

        // Portal team members for every client (batched in one pass), ordered pinned-first.
        let membersByAccountId = new Map();
        try {
          membersByAccountId = await fetchPortalUsersLinkedToAccounts(clients);
        } catch (e) {
          membersByAccountId = new Map();
        }
        const initialsFromName = (name) =>
          (name || '')
            .split(' ')
            .map((n) => n[0])
            .join('')
            .slice(0, 2)
            .toUpperCase() || '?';
        const getPinnedKey = (accountId) => {
          try {
            return localStorage.getItem(PINNED_CONTACT_STORAGE_PREFIX + accountId);
          } catch (e) {
            return null;
          }
        };
        const buildMembers = (account) => {
          let members = (membersByAccountId.get(account.id) || []).map((m) => ({
            id: m.uid,
            name: m.name,
            // Same resolution as Client Overview: CRM team-member override → portal profile
            // image → account contact photo (for the primary contact).
            photo:
              getCrmTeamMemberDisplayPhotoUrl(account, m.uid) ||
              m.profileImage ||
              (m.isPrimary ? getCrmContactDisplayPhotoUrl(account) : '') ||
              '',
            initials: initialsFromName(m.name),
            isPrimary: !!m.isPrimary,
            pinned: false,
          }));

          if (members.length === 0) {
            const cName = `${account.contact?.firstName || ''} ${account.contact?.lastName || ''}`.trim();
            if (cName) {
              members = [
                {
                  id: 'account-primary',
                  name: cName,
                  photo: getCrmContactDisplayPhotoUrl(account) || '',
                  initials: `${account.contact?.firstName?.[0] || ''}${account.contact?.lastName?.[0] || ''}`.toUpperCase() || '?',
                  isPrimary: true,
                  pinned: false,
                },
              ];
            }
          }

          // Pinned first: explicit uid match, else the primary contact when 'account-primary' is pinned.
          const pinnedKey = getPinnedKey(account.id);
          let idx = pinnedKey ? members.findIndex((m) => m.id === pinnedKey) : -1;
          if (idx < 0 && pinnedKey === 'account-primary') {
            idx = members.findIndex((m) => m.isPrimary);
          }
          if (idx >= 0) {
            members[idx].pinned = true;
            if (idx > 0) {
              const [p] = members.splice(idx, 1);
              members.unshift(p);
            }
          }
          return members;
        };

        let totalLimit = 0;
        let totalUsedThisMonth = 0;
        let totalRemaining = 0;
        const rows = [];

        for (const account of clients) {
          // 1:1 Strategy Call allowance. Every client's cycle resets on its own date, so we
          // track two numbers: what they've used inside their CURRENT cycle (which is what
          // determines how many they can still book) and what they've used inside this
          // CALENDAR month (which is what the meeting reports on).
          let used = 0;
          let usedThisMonth = 0;
          let limit = 0;
          let resetDate = null;
          const programStatus = account?.program?.status;
          if (programStatus && programStatus !== 'Not Started') {
            try {
              const contractItems = await getContractItems(programStatus);
              const context = findStrategyCallDeliverableContext(account, contractItems, itemsData, programStatus);
              if (context) {
                const usage = computeStrategyCallUsage(account, null, context);
                if (usage) {
                  used = usage.used || 0;
                  limit = usage.limit || 0;
                  resetDate = usage.periodEnd || null;
                }
                // Same logs computeStrategyCallUsage counts, re-bucketed by calendar month.
                const progress = resolveDeliverableProgress(account, context.rowId, context.catalogId);
                usedThisMonth = (progress.logs || []).reduce((sum, log) => {
                  const logDate =
                    parseFlexibleAccountDate(log.scheduledAt) ||
                    parseFlexibleAccountDate(log.date) ||
                    parseFlexibleAccountDate(log.timestamp);
                  return inThisMonth(logDate) ? sum + (log.quantity || 0) : sum;
                }, 0);
              }
            } catch (e) {
              // ignore per-client usage errors
            }
          }
          const remaining = Math.max(0, limit - used);
          totalLimit += limit;
          totalUsedThisMonth += usedThisMonth;
          totalRemaining += remaining;

          // How long they've been a client, from their real start date.
          const startDate = getClientActualStartDate(account, null);
          let monthsWithUs = null;
          if (startDate) {
            const months =
              (now.getFullYear() - startDate.getFullYear()) * 12 +
              (now.getMonth() - startDate.getMonth()) -
              (now.getDate() < startDate.getDate() ? 1 : 0);
            monthsWithUs = Math.max(0, months);
          }

          // This month's check-in, matched on accountId first then the contact email.
          const contactEmail = String(account.contact?.email || '').trim().toLowerCase();
          const checkIn =
            checkInsByAccountId.get(account.id) ||
            (contactEmail ? checkInsByEmail.get(contactEmail) : null) ||
            null;

          // Meetings — the client's calendar events, matched exactly like the Events
          // tab. Past 30 days → recentCalls ("Met with"); future → upcomingCalls.
          const recentCalls = [];
          const upcomingCalls = [];
          const roster = buildClientEventRoster(account, rosterByClient[account.id] || []);
          calendarEvents.forEach(({ event, fetchedBy }) => {
            if (!eventMatchesClientRoster(event, roster)) return;
            const date = new Date(event.start || event.startTime);
            if (isNaN(date.getTime())) return;
            const isFuture = date > now;
            if (!isFuture && date < thirtyDaysAgo) return;
            const host = resolveCalendarEventHost(event, fetchedBy, internalUsers);
            (isFuture ? upcomingCalls : recentCalls).push({
              date,
              label: host.name || event.title || event.summary || 'Meeting',
              photo: host.profileImage || '',
              initials: host.initials || '',
            });
          });

          recentCalls.sort((a, b) => b.date - a.date); // most recent first
          upcomingCalls.sort((a, b) => a.date - b.date); // soonest first

          // Next 3 roadmap steps (title + department)
          const roadmap = Array.isArray(account.roadmapTemplates) ? account.roadmapTemplates : [];
          const nextSteps = roadmap
            .filter((t) => {
              const s = (t.status || '').toLowerCase();
              return s !== 'completed' && s !== 'hidden';
            })
            .sort((a, b) => (a.order ?? a.allOrder ?? 0) - (b.order ?? b.allOrder ?? 0))
            .slice(0, 3)
            .map((t) => ({
              title: t.title || t.name || 'Untitled',
              department: t.department || t.type || 'General',
            }));

          rows.push({
            id: account.id,
            account,
            members: buildMembers(account),
            recentCalls,
            upcomingCalls,
            nextSteps,
            used,
            usedThisMonth,
            remaining,
            limit,
            resetDate,
            monthsWithUs,
            checkIn,
            contactEmail,
          });
        }

        // Recently active first; within each group sort by most-recent call
        rows.sort((a, b) => {
          const aActive = a.recentCalls.length > 0 ? 1 : 0;
          const bActive = b.recentCalls.length > 0 ? 1 : 0;
          if (aActive !== bActive) return bActive - aActive;
          const aDate = a.recentCalls[0]?.date?.getTime?.() || 0;
          const bDate = b.recentCalls[0]?.date?.getTime?.() || 0;
          return bDate - aDate;
        });

        if (!active) return;
        setClientActivity({
          rows,
          totals: { total: totalLimit, used: totalUsedThisMonth, remaining: totalRemaining },
        });
      } catch (e) {
        console.error('Error building client activity for MeetingMode:', e);
        if (active) setClientActivity({ rows: [], totals: { total: 0, used: 0, remaining: 0 } });
      } finally {
        if (active) setClientActivityLoading(false);
      }
    };

    loadClientActivity();
    return () => {
      active = false;
    };
  }, [allAccounts, internalUsers]);

  // Last email with each client — one Gmail lookup per client against the signed-in
  // user's mailbox, run a few at a time so the Clients slide isn't held up by it.
  useEffect(() => {
    let active = true;
    const rows = clientActivity.rows || [];
    if (!currentUser?.uid || rows.length === 0) return undefined;

    const loadLastEmails = async () => {
      setLastEmailStatus('loading');
      const targets = rows.filter((r) => r.contactEmail);
      const results = {};
      // Gmail's "connected" state comes back with every call; the first definitive
      // answer wins so a later per-client error can't read as "not connected".
      let overall = 'ok';
      const CONCURRENCY = 4;
      let cursor = 0;

      const worker = async () => {
        while (cursor < targets.length) {
          const row = targets[cursor++];
          if (!active) return;
          try {
            const { messages, status } = await fetchGmailMessagesForEmailWithStatus(
              currentUser.uid,
              row.contactEmail,
              1
            );
            if (status === 'not_connected') {
              overall = 'not_connected';
              return;
            }
            if (status !== 'ok') {
              overall = overall === 'ok' ? 'error' : overall;
              continue;
            }
            const latest = messages && messages[0];
            if (latest) {
              results[row.id] = {
                date: latest.date instanceof Date ? latest.date : new Date(latest.date),
                subject: latest.subject || '(No Subject)',
              };
            }
          } catch (e) {
            overall = overall === 'ok' ? 'error' : overall;
          }
        }
      };

      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, targets.length) }, worker));
      if (!active) return;
      setLastEmailByAccount(results);
      setLastEmailStatus(overall);
    };

    loadLastEmails();
    return () => {
      active = false;
    };
  }, [clientActivity.rows, currentUser?.uid]);

  // Create filtered data
  const nowForConferences = new Date();
  const filteredConferences = upcomingEvents.filter((c) => {
    const start = c.startTime ? new Date(c.startTime) : null;
    const end = c.endTime ? new Date(c.endTime) : null;
    if (!start || Number.isNaN(start.getTime())) return false;
    if (end && !Number.isNaN(end.getTime())) return end >= nowForConferences;
    return start >= nowForConferences;
  });
  const filteredClientVisits = clientVisits;

  // Group visits by date so companies sharing a date show under one date heading.
  const clientVisitGroups = (() => {
    const byDate = new Map();
    filteredClientVisits.forEach((v) => {
      const key = v.dayKey || `${v.day1}|${v.day2 || ''}`;
      if (!byDate.has(key)) {
        byDate.set(key, {
          key,
          day1: v.day1,
          day2: v.day2,
          dateLabel: v.dateLabel,
          isWithin7Days: false,
          types: [],
          companies: [],
        });
      }
      const g = byDate.get(key);
      if (v.isWithin7Days) g.isWithin7Days = true;
      if (v.visitType && !g.types.includes(v.visitType)) g.types.push(v.visitType);
      g.companies.push(v);
    });
    return Array.from(byDate.values()).sort((a, b) => String(a.day1).localeCompare(String(b.day1)));
  })();

  // Conference cards, soonest first, each with a days-away countdown.
  const conferenceCards = (() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return filteredConferences
      .map((c) => {
        const start = new Date(c.startTime);
        const startDay = new Date(start);
        startDay.setHours(0, 0, 0, 0);
        const daysAway = Math.round((startDay - today) / (24 * 60 * 60 * 1000));
        return {
          id: c.id || `${c.title}-${start.getTime()}`,
          title: c.title || 'Conference',
          location: c.location || '',
          start,
          daysAway,
          dateLabel: start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
          timeLabel: start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
        };
      })
      .sort((a, b) => a.start - b.start);
  })();

  // Growth — Last 30 Days. Computed once (not inside the slide's render) so the slide and
  // the saved meeting report are guaranteed to show the same numbers.
  const growth = useMemo(() => {
    const now = new Date();
    const since = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const inWindow = (d) => d instanceof Date && !Number.isNaN(d.getTime()) && d >= since && d <= now;

    // New leads in the last 30 days
    const newLeads = (leadCards || []).filter(
      (l) => l.status !== 'Lost' && inWindow(l.receivedDate || l.createdAt)
    );

    // Clients live on accounts; conversion happens in place so they keep leadSource —
    // and keep the ORIGINAL lead's createdAt, which is why "new" is measured from the
    // client's actual start date (program start → contract start → billing period)
    // rather than when the account document was first created.
    const clientAccts = (allAccounts || []).filter((a) => a && (a.type === 'Clients' || a.type === 'clients'));
    const newClients = clientAccts.filter((a) => {
      if (!isActiveClientAccount(a)) return false;
      return inWindow(getClientActualStartDate(a, null));
    });
    const lostClients = clientAccts.filter((a) => {
      const lc = resolveClientLifecycle(a);
      if (!lc.isEnded) return false;
      return inWindow(lc.endedAt || toDateSafe(a.updatedAt || a.updated_at || a.lastActivityAt || a.lastActivity_at || a.createdAt));
    });

    const SOURCE_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#F87171', '#8884d8'];
    const bySource = (records, get) => {
      const groups = {};
      records.forEach((r) => {
        const source = (get(r) || 'Unknown').toString().trim() || 'Unknown';
        groups[source] = (groups[source] || 0) + 1;
      });
      return Object.entries(groups)
        .sort((a, b) => b[1] - a[1])
        .map(([name, value], index) => ({ name, value, color: SOURCE_COLORS[index % SOURCE_COLORS.length] }));
    };

    // Website entries falling inside the last 30 days
    const websiteEntries = (marketingData?.websiteEntries || []).filter((entry) => {
      let entryDate = entry.createdAt;
      if (entry.date && entry.date.toDate) {
        entryDate = entry.date.toDate();
      } else if (entry.date instanceof Date) {
        entryDate = entry.date;
      } else if (entry.reportStartDate && entry.reportStartDate.toDate) {
        entryDate = entry.reportStartDate.toDate();
      } else if (entry.reportStartDate instanceof Date) {
        entryDate = entry.reportStartDate;
      }
      if (!entryDate) return false;
      return entryDate >= since && entryDate <= now;
    });
    const totalVisitors = websiteEntries.reduce((sum, entry) => sum + (parseInt(entry.visitors) || 0), 0);
    const durationEntries = websiteEntries.filter((entry) => parseFloat(entry.sessionDuration) > 0);
    const avgSeconds = durationEntries.length
      ? durationEntries.reduce((sum, entry) => sum + (parseFloat(entry.sessionDuration) || 0), 0) / durationEntries.length
      : 0;
    const bounceEntries = websiteEntries.filter((entry) => parseFloat(entry.bounceRate) > 0);
    const avgBounce = bounceEntries.length
      ? bounceEntries.reduce((sum, entry) => sum + (parseFloat(entry.bounceRate) || 0), 0) / bounceEntries.length
      : 0;

    // Social followers per platform, with the change against the newest entry at least 30 days old
    const socialEntries = marketingData?.socialEntries || [];
    const platforms = ['Facebook', 'Instagram', 'TikTok', 'YouTube', 'LinkedIn'];
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const entryDateOf = (entry) => {
      let d = entry.createdAt;
      if (d && d.toDate) d = d.toDate();
      if (!d && entry.year && entry.month) {
        d = new Date(parseInt(entry.year), months.indexOf(entry.month), 1);
      }
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
    };
    const social = platforms.map((platform) => {
      const platformEntries = socialEntries.filter((entry) => entry.platform === platform);
      let followers = 0;
      let change = null;

      if (platformEntries.length > 0) {
        const sortedEntries = platformEntries.slice().sort((a, b) => {
          const da = entryDateOf(a);
          const db = entryDateOf(b);
          if (!da && !db) return 0;
          if (!da) return 1;
          if (!db) return -1;
          return db.getTime() - da.getTime();
        });

        followers = parseInt(sortedEntries[0].followers) || 0;

        if (sortedEntries.length > 1) {
          // Compare against the newest entry that is at least 30 days old
          let previousEntry = sortedEntries.slice(1).find((entry) => {
            const d = entryDateOf(entry);
            return d && d <= since;
          });
          if (!previousEntry) previousEntry = sortedEntries[1];

          const diff = followers - (parseInt(previousEntry.followers) || 0);
          if (diff !== 0) {
            change = { value: Math.abs(diff), isIncrease: diff > 0 };
          }
        }
      }
      return { platform, followers, change };
    });

    return {
      newLeads: newLeads.length,
      newClients: newClients.length,
      lostClients: lostClients.length,
      leadSourceSlices: bySource(newLeads, (l) => l.source),
      clientSourceSlices: bySource(newClients, (a) => a.leadSource),
      website: { totalVisitors, avgSeconds, avgBounce, entryCount: websiteEntries.length },
      social,
      hasSocialData: socialEntries.length > 0,
    };
  }, [leadCards, allAccounts, marketingData]);

  const sections = [
    {
      title: 'Overview',
      icon: '🧭',
      isGrid: true,
      gridItems: [
        // Conferences — rendered as cards with a days-away countdown
        {
          title: `Conferences (${filteredConferences.length})`,
          icon: '🎤',
          conferences: conferenceCards,
        },
        // Client Visits — grouped by date, each company shown with its logo + name.
        {
          title: `Client Visits (${filteredClientVisits.length})`,
          icon: '🚐',
          visitGroups: clientVisitGroups,
        },
      ]
    },
    {
      title: 'Growth — Last 30 Days',
      icon: '📈',
      isGrowth: true
    },
    {
      title: 'Clients',
      icon: '🏢',
      isClients: true
    },
    {
      title: 'Outstanding Tasks',
      icon: '✅',
      isTasks: true
    },
    {
      title: 'Additional Items',
      icon: '📝',
      isAdditionalItems: true
    }
  ];

  // The saved report is a SUMMARY of what the five slides showed, not a snapshot of every
  // source document. Firestore caps a document at 1 MiB, so each slide is projected down to
  // exactly the fields meetings-reports renders and the long lists are capped. `reportVersion: 2`
  // marks reports saved from this Meeting Mode; meetings-reports still opens the older
  // nine-slide reports (goals / wins / roadmap / finance) in a legacy layout.
  const buildMeetingReportData = () => {
    try {
      const now = new Date();
      const iso = (value) => {
        const d = toDateSafe(value);
        return d ? d.toISOString() : null;
      };
      const text = (value, max = 300) => {
        const s = value == null ? '' : String(value);
        return s.length > max ? `${s.slice(0, max)}…` : s;
      };
      const url = (value) => (typeof value === 'string' && value.trim() ? value.trim().slice(0, 1000) : '');

      const conferenceSummary = (c) => ({
        id: c.id || null,
        title: text(c.title, 200),
        location: text(c.location, 200),
        startTime: iso(c.start),
        daysAway: Number.isFinite(c.daysAway) ? c.daysAway : null,
        dateLabel: c.dateLabel || '',
        timeLabel: c.timeLabel || '',
      });

      const visitGroupSummary = (g) => ({
        key: g.key,
        day1: g.day1 || null,
        day2: g.day2 || null,
        dateLabel: g.dateLabel || '',
        isWithin7Days: !!g.isWithin7Days,
        types: (g.types || []).slice(0, 4),
        companies: (g.companies || []).slice(0, 6).map((c) => ({
          id: c.id || null,
          company: text(c.company, 120),
          logo: url(c.logo),
          contactName: text(c.contactName, 120),
          contactPhoto: url(c.contactPhoto),
          visitType: c.visitType || null,
          status: c.status || null,
        })),
      });

      const sessionSummary = (s) => ({
        id: s.id || null,
        date: iso(s.date),
        label: s.label || '',
        eventName: text(s.eventName, 160),
        presenter: text(s.presenter, 80),
        presenterPhoto: url(s.presenterPhoto),
        presenterInitials: s.presenterInitials || '',
        department: s.department || 'General',
        startTime: s.startTime || '',
        endTime: s.endTime || '',
        needPresenter: !!s.needPresenter,
      });

      // Clients — one row per client card, with the logo already resolved.
      const personSummary = (p) => ({
        id: p.id || null,
        name: text(p.name, 80),
        photo: url(p.photo),
        initials: p.initials || '',
        pinned: !!p.pinned,
      });
      const callSummary = (c) => ({
        date: iso(c.date),
        label: text(c.label, 120),
        photo: url(c.photo),
        initials: c.initials || '',
      });
      const clientRowSummary = (r) => ({
        id: r.id,
        company: text(r.account?.company, 120),
        stage: r.account?.stage || null,
        logo: url(getCrmCompanyLogoDisplayUrl(r.account)),
        used: r.used || 0,
        usedThisMonth: r.usedThisMonth || 0,
        remaining: r.remaining || 0,
        limit: r.limit || 0,
        resetDate: iso(r.resetDate),
        monthsWithUs: r.monthsWithUs == null ? null : r.monthsWithUs,
        checkIn: r.checkIn
          ? {
              responded: !!r.checkIn.responded,
              rating: r.checkIn.rating == null ? null : r.checkIn.rating,
              status: text(r.checkIn.status, 60),
              comments: text(r.checkIn.comments, 300),
            }
          : null,
        lastEmailDate: iso(lastEmailByAccount[r.id]?.date),
        members: (r.members || []).slice(0, 8).map(personSummary),
        recentCalls: (r.recentCalls || []).slice(0, 4).map(callSummary),
        upcomingCalls: (r.upcomingCalls || []).slice(0, 4).map(callSummary),
        nextSteps: (r.nextSteps || []).slice(0, 3).map((s) => ({
          title: text(s.title, 120),
          department: s.department || 'General',
        })),
      });

      // Outstanding Tasks — keyed by user; the report pages through them 3 at a time.
      const userSummary = (u) => ({
        id: u.id || null,
        name: text(u.name, 80),
        initials: u.initials || '',
        photoURL: url(pickProfilePicUrl(u)),
      });
      const taskSummary = (t) => ({
        id: t.id || null,
        title: text(t.title, 160),
        dueDate: iso(t.dueDate),
      });
      const tasksByUserSummary = {};
      (internalUsers || []).slice(0, 30).forEach((u) => {
        const list = (tasksByUser || {})[u.id] || [];
        if (list.length > 0) tasksByUserSummary[u.id] = list.slice(0, 12).map(taskSummary);
      });

      const slices = (arr) => (arr || []).map((s) => ({ name: text(s.name, 80), value: s.value || 0 }));

      return {
        reportVersion: 2,
        createdAt: Timestamp.fromDate(now),
        createdAtISO: now.toISOString(),
        createdBy: currentUser?.uid || null,
        // Overview
        conferences: conferenceCards.slice(0, 25).map(conferenceSummary),
        clientVisitGroups: clientVisitGroups.slice(0, 20).map(visitGroupSummary),
        breakthroughSessions: (breakthroughSessions || []).slice(0, 8).map(sessionSummary),
        // Growth — Last 30 Days
        growth: {
          newLeads: growth.newLeads,
          newClients: growth.newClients,
          lostClients: growth.lostClients,
          leadSourceSlices: slices(growth.leadSourceSlices),
          clientSourceSlices: slices(growth.clientSourceSlices),
          website: {
            totalVisitors: growth.website.totalVisitors || 0,
            avgSeconds: growth.website.avgSeconds || 0,
            avgBounce: growth.website.avgBounce || 0,
            entryCount: growth.website.entryCount || 0,
          },
          social: (growth.social || []).map((s) => ({
            platform: s.platform,
            followers: s.followers || 0,
            change: s.change ? { value: s.change.value, isIncrease: !!s.change.isIncrease } : null,
          })),
          hasSocialData: !!growth.hasSocialData,
        },
        // Clients
        clientActivity: {
          monthLabel,
          totals: {
            total: clientActivity.totals?.total || 0,
            used: clientActivity.totals?.used || 0,
            remaining: clientActivity.totals?.remaining || 0,
          },
          rows: (clientActivity.rows || []).slice(0, 60).map(clientRowSummary),
        },
        // Outstanding Tasks
        internalUsers: (internalUsers || []).slice(0, 30).map(userSummary),
        tasksByUser: tasksByUserSummary,
        // Additional items
        questions: (questions || []).slice(0, 50).map((q) => text(q, 1000)),
        comments: (comments || []).slice(0, 50).map((c) => text(c, 1000)),
      };
    } catch (e) {
      console.error('Failed to build meeting report payload:', e);
      return null;
    }
  };

  const handleFinish = async () => {
    try {
      // A slide that is still loading would be saved empty — let the presenter decide.
      const stillLoading = [
        clientActivityLoading && 'Clients',
        marketingLoading && 'Growth',
        sessionsLoading && 'Breakthrough Sessions',
      ].filter(Boolean);
      if (
        stillLoading.length > 0 &&
        !window.confirm(`Still loading: ${stillLoading.join(', ')}. Save the report without that data?`)
      ) {
        return;
      }

      setIsSaving(true);
      const payload = buildMeetingReportData();
      if (!payload) {
        alert('Could not build the meeting report. Please try again.');
        return;
      }
      // Firestore rejects documents over 1 MiB. The payload is already projected down, but
      // measure it anyway so an unexpectedly large report fails with something readable.
      const approxBytes = new Blob([JSON.stringify(payload)]).size;
      if (approxBytes > 900 * 1024) {
        console.error('Meeting report payload too large:', approxBytes, 'bytes');
        alert(
          `This meeting report is too large to save (${Math.round(approxBytes / 1024)} KB, limit is 1024 KB). ` +
            'Please let the team know so the report summary can be trimmed further.'
        );
        return;
      }
      await addDoc(collection(db, 'meetingReports'), payload);
      navigate('/crm/meetings-reports');
    } catch (e) {
      console.error('Failed to save meeting report:', e);
      alert('Failed to save meeting report. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePrevious = () => {
    setCurrentSection((prev) => (prev > 0 ? prev - 1 : sections.length - 1));
  };

  const handleNext = () => {
    setCurrentSection((prev) => (prev < sections.length - 1 ? prev + 1 : 0));
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (taskPageDropdownOpen && !event.target.closest('.task-page-dropdown')) {
        setTaskPageDropdownOpen(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [taskPageDropdownOpen]);

  return (
    <div className="min-h-screen p-6">
      {/* Same ambient BBA background the start dashboard uses */}
      <PortalBackground />
      <div className="relative max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8 relative">
          <button
            onClick={() => navigate('/crm/meetings-reports')}
            className="px-3 py-2 rounded-lg bg-white dark:bg-gray-800 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors z-10 text-lg leading-none"
            title="Back to meeting reports"
          >
            ⬅️
          </button>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full text-center">Meeting Mode</h1>
          <div className="w-11" />
        </div>

        <div className="flex items-center justify-between mb-8">
          <button
            onClick={handlePrevious}
            disabled={currentSection === 0}
            className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
              currentSection === 0 
                ? 'text-gray-400 cursor-not-allowed bg-gray-100 dark:bg-gray-800' 
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 bg-white dark:bg-gray-800 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <span aria-hidden="true">⬅️</span>
            <span className="ml-2">Previous</span>
          </button>
          <span className="text-lg font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 px-4 py-2 rounded-lg shadow-sm">
            {currentSection + 1} of {sections.length}
          </span>
          {currentSection === sections.length - 1 ? (
            <button
              onClick={handleFinish}
              disabled={isSaving}
              className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                isSaving
                  ? 'text-gray-400 cursor-not-allowed bg-gray-100 dark:bg-gray-800'
                  : 'text-white bg-red-600 hover:bg-red-700 shadow-sm'
              }`}
            >
              <span className="mr-2">{isSaving ? 'Saving…' : 'Finish'}</span>
            </button>
          ) : (
            <button
              onClick={handleNext}
              className={`flex items-center px-4 py-2 rounded-lg transition-colors text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 bg-white dark:bg-gray-800 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700`}
            >
              <span className="mr-2">Next</span>
              <span aria-hidden="true">➡️</span>
            </button>
          )}
        </div>

        <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex flex-col items-center mb-6">
            <span className="text-3xl leading-none" aria-hidden="true">{sections[currentSection].icon}</span>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mt-2 text-center">
              {sections[currentSection].title}
            </h2>
          </div>
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <BBALoader size={32} />
              <span className="ml-3 text-gray-600 dark:text-gray-400">Loading...</span>
            </div>
          ) : sections[currentSection].isGrid ? (
            <>
              {/* Top Row: Conferences and Visits */}
              <div className="grid grid-cols-2 gap-6 mb-6">
                {sections[currentSection].gridItems.slice(0, 2).map((gridItem, gridIndex) => (
                  <div key={gridIndex} className="bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 rounded-t-lg p-4">
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-2xl leading-none" aria-hidden="true">{gridItem.icon}</span>
                        <h3 className="text-red-700 dark:text-red-300 font-semibold text-base">
                          {gridItem.title}
                        </h3>
                      </div>
                    </div>
                    <div className="p-4 space-y-3">
                      {gridItem.visitGroups ? (
                        <VisitGroups groups={gridItem.visitGroups} />
                      ) : gridItem.conferences ? (
                        gridItem.conferences.length === 0 ? (
                          <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                            No upcoming conferences
                          </p>
                        ) : (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {gridItem.conferences.slice(0, 6).map((c) => {
                              const soon = c.daysAway <= 7;
                              return (
                                <div
                                  key={c.id}
                                  className={`flex items-center gap-3 rounded-lg border p-3 ${
                                    soon
                                      ? 'border-amber-300 dark:border-amber-600 bg-amber-50/70 dark:bg-amber-900/20'
                                      : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800'
                                  }`}
                                >
                                  <div
                                    className={`flex-shrink-0 w-16 rounded-lg py-2 text-center ${
                                      soon
                                        ? 'bg-amber-100 dark:bg-amber-900/40'
                                        : 'bg-gray-100 dark:bg-gray-900/40'
                                    }`}
                                  >
                                    <div className="text-2xl font-bold leading-none text-gray-900 dark:text-white">
                                      {c.daysAway <= 0 ? '🎉' : c.daysAway}
                                    </div>
                                    <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mt-1">
                                      {c.daysAway <= 0 ? 'today' : c.daysAway === 1 ? 'day away' : 'days away'}
                                    </div>
                                  </div>
                                  <div className="min-w-0 flex-1 text-left">
                                    <div className="text-sm font-semibold text-gray-800 dark:text-gray-100 break-words leading-snug">
                                      {c.title}
                                    </div>
                                    <div className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
                                      📅 {c.dateLabel} · {c.timeLabel}
                                    </div>
                                    {c.location && (
                                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 break-words">
                                        📍 {c.location}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>

              {/* Bottom Row: next 8 Breakthrough Sessions, same themed background as the
                  CRM dashboard and the client portal's Next Breakthrough Session hero */}
              <div className="grid grid-cols-4 gap-4">
                {sessionsLoading ? (
                  <div className="col-span-4 text-center text-gray-500 dark:text-gray-400 py-8">
                    Loading breakthrough sessions...
                  </div>
                ) : (
                  Array.from({ length: 8 }, (_, idx) => {
                    const session = breakthroughSessions[idx];
                    if (!session) {
                      return (
                        <div
                          key={`empty-${idx}`}
                          className="bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 p-3 flex flex-col gap-2 opacity-50"
                        >
                          <div className="flex items-center gap-2">
                            <div className="h-10 w-10 rounded-full bg-gray-300 dark:bg-gray-600"></div>
                            <div className="flex-1">
                              <div className="text-xs text-gray-400 dark:text-gray-500">No session</div>
                              <div className="text-xs text-gray-400 dark:text-gray-500">—</div>
                            </div>
                          </div>
                          <div className="text-sm text-gray-400 dark:text-gray-500">No upcoming session</div>
                        </div>
                      );
                    }
                    const isNeedPresenter = session.needPresenter;
                    const theme = getBreakthroughTheme(session.department);
                    const initials = session.presenterInitials || initialsFor(session.presenter, session.label?.slice(0, 2).toUpperCase() || 'BS');

                    return (
                      <div
                        key={session.id || idx}
                        className="relative overflow-hidden rounded-lg text-white shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                        style={{ background: theme.gradient }}
                        onClick={() => navigate('/crm/fulfillment/breakthroughsfufillment')}
                      >
                        <BreakthroughSessionBackground department={session.department} />
                        <div className="relative z-10 p-3 flex flex-col gap-2">
                          <div className="flex items-center gap-2">
                            {session.presenterPhoto ? (
                              <img
                                src={session.presenterPhoto}
                                alt={session.presenter || 'Presenter'}
                                className="h-10 w-10 rounded-full object-cover ring-2 ring-white/40"
                              />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-white/20 ring-2 ring-white/40 flex items-center justify-center text-white font-semibold text-xs">
                                {initials}
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="text-xs text-white/70">{session.label}</div>
                              <div className="text-xs text-white/85">{formatShortDate(session.date)}</div>
                            </div>
                            <span className="inline-flex shrink-0 px-2 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white">
                              {session.department || 'Unassigned'}
                            </span>
                          </div>

                          <div className="flex flex-col">
                            {isNeedPresenter ? (
                              <>
                                <div className="text-sm font-semibold text-white flex items-center gap-1">
                                  <span>⚠️</span>
                                  <span>Need Presenter</span>
                                </div>
                                <div className="text-xs text-white/85 mt-1">
                                  {formatShortDate(session.date)} • {session.startTime || ''} {session.startTime && session.endTime ? '-' : ''} {session.endTime || ''}
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="text-sm font-semibold text-white">
                                  {session.eventName}
                                </div>
                                <div className="text-xs text-white/85 mt-1">
                                  {session.presenter ? `Presenter: ${session.presenter}` : 'No presenter yet'}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          ) : sections[currentSection].isGrowth ? (
            (() => {
              const renderSourcePie = (heading, emoji, slices) => {
                const total = slices.reduce((sum, item) => sum + (item.value || 0), 0);
                let currentPercent = 0;
                const gradientParts = slices.map((item) => {
                  const percent = (item.value / total) * 100;
                  const part = `${item.color} ${currentPercent}% ${currentPercent + percent}%`;
                  currentPercent += percent;
                  return part;
                });

                return (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 rounded-t-lg p-4">
                      <h3 className="text-red-700 dark:text-red-300 font-semibold text-base text-center">
                        <span className="mr-2" aria-hidden="true">{emoji}</span>{heading}
                      </h3>
                    </div>
                    <div className="p-6">
                      {total === 0 ? (
                        <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                          No lead source data in the last 30 days
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-8 flex-wrap">
                          <div
                            className="w-40 h-40 rounded-full shadow-inner flex-shrink-0"
                            role="img"
                            aria-label={`${heading} pie chart`}
                            style={{ background: `conic-gradient(${gradientParts.join(', ')})` }}
                          ></div>
                          <div className="space-y-2 text-sm text-gray-700 dark:text-gray-200">
                            {slices.map((item, index) => (
                              <div key={index} className="flex items-center gap-3">
                                <span className="inline-block w-3 h-3 rounded-sm" style={{ background: item.color }}></span>
                                <span className="min-w-[140px]">{item.name}</span>
                                <span className="font-medium">{item.value}</span>
                                <span className="text-gray-500 dark:text-gray-400">
                                  ({((item.value / total) * 100).toFixed(0)}%)
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              };

              const { totalVisitors, avgSeconds, avgBounce } = growth.website;

              const statCard = (emoji, label, value, valueClass) => (
                <div className="rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 p-4 text-center">
                  <div className="text-3xl leading-none" aria-hidden="true">{emoji}</div>
                  <div className={`mt-2 text-4xl font-bold ${valueClass}`}>{value}</div>
                  <div className="mt-1 text-sm font-medium text-gray-700 dark:text-gray-300">{label}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">last 30 days</div>
                </div>
              );

              return (
                <div className="space-y-8">
                  {/* Headline counts */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {statCard('🌱', 'New leads', growth.newLeads, 'text-green-600 dark:text-green-400')}
                    {statCard('🤝', 'New clients', growth.newClients, 'text-blue-600 dark:text-blue-400')}
                    {statCard('👋', 'Lost clients', growth.lostClients, 'text-red-600 dark:text-red-400')}
                  </div>
                  <p className="-mt-6 text-xs text-center text-gray-500 dark:text-gray-400">
                    New clients are counted from their start date; lost clients from when the account was last updated.
                  </p>

                  {/* Lead source for the same 30-day window, leads and clients */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {renderSourcePie('Lead Source — New Leads', '🌱', growth.leadSourceSlices)}
                    {renderSourcePie('Lead Source — New Clients', '🤝', growth.clientSourceSlices)}
                  </div>

                  {marketingLoading ? (
                    <div className="flex justify-center items-center py-8">
                      <BBALoader size={32} />
                      <span className="ml-3 text-gray-600 dark:text-gray-400">Loading marketing data...</span>
                    </div>
                  ) : (
                    <>
                      {/* Website & Organic Traffic */}
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                        <div className="bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 rounded-t-lg p-4">
                          <h3 className="text-red-700 dark:text-red-300 font-semibold text-base text-center">
                            <span className="mr-2" aria-hidden="true">🌐</span>Website &amp; Organic Traffic
                          </h3>
                        </div>
                        <div className="p-6">
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div className="rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 p-4 text-center">
                              <div className="text-sm font-medium text-gray-700 dark:text-gray-300">👥 Total Visitors</div>
                              <div className="mt-1 text-3xl font-bold text-gray-900 dark:text-white">
                                {totalVisitors.toLocaleString()}
                              </div>
                              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">last 30 days</div>
                            </div>
                            <div className="rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 p-4 text-center">
                              <div className="text-sm font-medium text-gray-700 dark:text-gray-300">⏱️ Avg. Session Duration</div>
                              <div className="mt-1 text-3xl font-bold text-gray-900 dark:text-white">
                                {avgSeconds < 60 ? `${Math.round(avgSeconds)}s` : `${(avgSeconds / 60).toFixed(1)}m`}
                              </div>
                              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">last 30 days</div>
                            </div>
                            <div className="rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 p-4 text-center">
                              <div className="text-sm font-medium text-gray-700 dark:text-gray-300">↩️ Bounce Rate</div>
                              <div className="mt-1 text-3xl font-bold text-gray-900 dark:text-white">
                                {`${avgBounce.toFixed(1)}%`}
                              </div>
                              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">last 30 days</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Social Media Performance */}
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                        <div className="bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 rounded-t-lg p-4">
                          <h3 className="text-red-700 dark:text-red-300 font-semibold text-base text-center">
                            <span className="mr-2" aria-hidden="true">📱</span>Social Media Performance
                          </h3>
                        </div>
                        <div className="p-6">
                          {growth.hasSocialData ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                              {(() => {
                                return growth.social.map(({ platform, followers, change }) => {
                                  const borderColors = {
                                    'Facebook': 'border-blue-500',
                                    'Instagram': 'border-pink-500',
                                    'TikTok': 'border-black',
                                    'YouTube': 'border-red-500',
                                    'LinkedIn': 'border-blue-600'
                                  };
                                  const textColors = {
                                    'Facebook': darkMode ? 'text-blue-300' : 'text-blue-600',
                                    'Instagram': darkMode ? 'text-pink-300' : 'text-pink-600',
                                    'TikTok': darkMode ? 'text-gray-300' : 'text-gray-700',
                                    'YouTube': darkMode ? 'text-red-300' : 'text-red-600',
                                    'LinkedIn': darkMode ? 'text-blue-300' : 'text-blue-600'
                                  };
                                  // Map platform names to correct filenames (case-sensitive for hosting)
                                  const platformIcons = {
                                    'Facebook': '/facebook.svg',
                                    'Instagram': '/instagram.svg',
                                    'TikTok': '/tiktok.svg',
                                    'YouTube': '/Youtube.svg',
                                    'LinkedIn': '/Linkedin.svg'
                                  };

                                  return (
                                    <div key={platform} className={`p-4 rounded-lg shadow-md border-l-4 ${borderColors[platform]} relative ${
                                      darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
                                    }`}>
                                      <div className="flex flex-col items-center">
                                        <div className="w-16 h-16 mb-3 flex items-center justify-center">
                                          <img
                                            src={platformIcons[platform] || `/${platform.toLowerCase()}.svg`}
                                            alt={`${platform} icon`}
                                            className="w-14 h-14"
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                          />
                                        </div>
                                        <h3 className={`text-sm font-bold text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>{platform}</h3>
                                        <div className={`text-lg font-bold mt-2 ${textColors[platform]}`}>{followers.toLocaleString()}</div>
                                        <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>followers</div>
                                        {change && (
                                          <div className={`flex items-center gap-1.5 mt-2 px-3 py-1.5 rounded-full border-2 font-semibold text-sm ${
                                            change.isIncrease
                                              ? 'text-green-700 dark:text-green-300 border-green-600 dark:border-green-400 bg-green-50 dark:bg-green-900/30'
                                              : 'text-red-700 dark:text-red-300 border-red-600 dark:border-red-400 bg-red-50 dark:bg-red-900/30'
                                          }`}>
                                            <span aria-hidden="true">{change.isIncrease ? '⬆️' : '⬇️'}</span>
                                            <span>{change.value.toLocaleString()}</span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                });
                              })()}
                            </div>
                          ) : (
                            <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                              No social media data available
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })()
          ) : sections[currentSection].isClients ? (
            <div className="space-y-6">
              {/* Pooled 1:1 Strategy Call numbers. Allowances reset on each client's own
                  anniversary, so "used" is counted over the calendar month while
                  "remaining" is what is genuinely still bookable in each live cycle. */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 p-4 text-center">
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">📅 Allotted</div>
                  <div className="mt-1 text-3xl font-bold text-gray-900 dark:text-white">{clientActivity.totals.total}</div>
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">across all live cycles</div>
                </div>
                <div className="rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 p-4 text-center">
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">✅ Used in {monthLabel}</div>
                  <div className="mt-1 text-3xl font-bold text-blue-600 dark:text-blue-400">{clientActivity.totals.used}</div>
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">calls logged this month</div>
                </div>
                <div className="rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 p-4 text-center">
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">🎟️ Still bookable</div>
                  <div className="mt-1 text-3xl font-bold text-green-600 dark:text-green-400">{clientActivity.totals.remaining}</div>
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">left before each reset</div>
                </div>
              </div>
              <p className="text-xs text-center text-gray-500 dark:text-gray-400">
                Every client resets on their own date, so allotted ≠ used + bookable. Each card shows its own reset day.
              </p>

              {clientActivityLoading ? (
                <div className="flex justify-center items-center py-8">
                  <BBALoader size={32} />
                  <span className="ml-3 text-gray-600 dark:text-gray-400">Loading client activity...</span>
                </div>
              ) : clientActivity.rows.length === 0 ? (
                <div className="text-center text-gray-500 dark:text-gray-400 py-8">No active clients found</div>
              ) : (
                [
                  { key: 'active', title: 'Recently Active', subtitle: 'Met in the last 30 days', match: (r) => r.recentCalls.length > 0 },
                  { key: 'attention', title: 'Needs Attention', subtitle: 'No 1:1 call in the last 30 days', match: (r) => r.recentCalls.length === 0 },
                ].map((group) => {
                  const groupRows = clientActivity.rows.filter(group.match);
                  if (groupRows.length === 0) return null;
                  return (
                    <div key={group.key} className="space-y-3">
                      <div className="flex items-center justify-center gap-2">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center">{group.title}</h3>
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">{groupRows.length}</span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 text-center">{group.subtitle}</p>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {groupRows.map(({ id, account, members, recentCalls, upcomingCalls, nextSteps, used, limit, resetDate, monthsWithUs, checkIn }) => (
                          <div
                            key={id}
                            className={`rounded-lg border p-4 ${
                              group.key === 'attention'
                                ? 'border-amber-300 dark:border-amber-700 bg-amber-50/40 dark:bg-amber-900/10'
                                : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800'
                            }`}
                          >
                            {/* Header: company logo + name + large 1:1 call allotment */}
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex items-center gap-3 min-w-0">
                                {getCrmCompanyLogoDisplayUrl(account) ? (
                                  <div className="w-12 h-12 shrink-0 rounded-full overflow-hidden bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 flex items-center justify-center">
                                    <img
                                      src={getCrmCompanyLogoDisplayUrl(account)}
                                      alt=""
                                      className="max-w-[88%] max-h-[88%] object-contain"
                                    />
                                  </div>
                                ) : null}
                                <div className="min-w-0">
                                  <div className="text-lg font-semibold text-gray-800 dark:text-gray-100 truncate">
                                    {account.company || 'Unknown'}
                                  </div>
                                  {account.stage && (
                                    <span
                                      className="inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded-full"
                                      style={{
                                        border: `1.5px solid ${clientProgramColors[account.stage] || '#6B7280'}`,
                                        color: clientProgramColors[account.stage] || '#6B7280',
                                      }}
                                    >
                                      {account.stage}
                                    </span>
                                  )}
                                </div>
                              </div>
                              {limit > 0 && (
                                <div className="flex-shrink-0 text-center rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/40 px-4 py-2">
                                  <div className="text-4xl font-bold leading-none text-gray-900 dark:text-white">
                                    {used}
                                    <span className="text-gray-400 dark:text-gray-500">/</span>
                                    {limit}
                                  </div>
                                  <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mt-1">1:1 calls</div>
                                  {resetDate && (
                                    <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5 whitespace-nowrap">
                                      🔄 resets {formatShortDate(resetDate)}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Tenure · this month's check-in · last email touch */}
                            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                                ⏳ {monthsWithUs == null
                                  ? 'Start date unknown'
                                  : monthsWithUs === 0
                                  ? 'First month with us'
                                  : `${monthsWithUs} ${monthsWithUs === 1 ? 'month' : 'months'} with us`}
                              </span>
                              <span
                                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${
                                  !checkIn
                                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                                    : checkIn.responded
                                    ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
                                    : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200'
                                }`}
                                title={checkIn?.comments || ''}
                              >
                                {!checkIn
                                  ? `📝 No ${monthLabel} check-in`
                                  : checkIn.responded
                                  ? `⭐ ${monthLabel} check-in: ${checkIn.rating != null ? `${checkIn.rating}/5` : checkIn.status}`
                                  : `⏰ ${monthLabel} check-in sent, no reply`}
                              </span>
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                                {lastEmailStatus === 'not_connected'
                                  ? '✉️ Gmail not connected'
                                  : lastEmailStatus === 'loading'
                                  ? '✉️ Checking email…'
                                  : lastEmailByAccount[id]
                                  ? `✉️ Last email ${lastEmailByAccount[id].date.toLocaleDateString()}`
                                  : lastEmailStatus === 'error'
                                  ? "✉️ Couldn't load email"
                                  : '✉️ No email found'}
                              </span>
                            </div>

                            {/* Members — pinned first, large profile pics */}
                            {members.length > 0 && (
                              <div className="mt-4 flex flex-wrap gap-4">
                                {members.map((m) => (
                                  <div key={m.id} className="flex flex-col items-center w-20 text-center">
                                    <div className="relative">
                                      {m.photo ? (
                                        <img
                                          src={m.photo}
                                          alt={m.name}
                                          className={`w-14 h-14 rounded-full object-cover ${m.pinned ? 'ring-2 ring-red-500' : 'ring-1 ring-gray-200 dark:ring-gray-600'}`}
                                          onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }}
                                        />
                                      ) : null}
                                      <div
                                        className={`w-14 h-14 rounded-full bg-gray-300 dark:bg-gray-600 items-center justify-center text-base font-semibold text-gray-700 dark:text-gray-200 ${m.pinned ? 'ring-2 ring-red-500' : 'ring-1 ring-gray-200 dark:ring-gray-600'}`}
                                        style={{ display: m.photo ? 'none' : 'flex' }}
                                      >
                                        {m.initials}
                                      </div>
                                      {m.pinned && (
                                        <span className="absolute -top-1 -right-1 text-xs leading-none" aria-hidden="true">
                                          📌
                                        </span>
                                      )}
                                    </div>
                                    <span className="mt-1 text-xs text-gray-700 dark:text-gray-200 w-full break-words leading-tight" title={m.name}>{m.name}</span>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Who they've met with (last 30 days) — prominent */}
                            <div className="mt-4">
                              <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                                <span aria-hidden="true">📞</span> Met with · last 30 days
                              </div>
                              {recentCalls.length === 0 ? (
                                <div className="text-sm text-amber-700 dark:text-amber-400">No calls logged</div>
                              ) : (
                                <div className="space-y-2">
                                  {recentCalls.slice(0, 4).map((c, i) => (
                                    <div key={i} className="flex items-center justify-between gap-2">
                                      <div className="flex items-center gap-2.5 min-w-0">
                                        {c.photo ? (
                                          <img
                                            src={c.photo}
                                            alt={c.label}
                                            className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                                            onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }}
                                          />
                                        ) : null}
                                        <div
                                          className="w-9 h-9 rounded-full bg-gray-300 dark:bg-gray-600 items-center justify-center text-xs font-medium text-gray-700 dark:text-gray-200 flex-shrink-0"
                                          style={{ display: c.photo ? 'none' : 'flex' }}
                                        >
                                          {c.initials || '📞'}
                                        </div>
                                        <span className="text-base text-gray-800 dark:text-gray-100 truncate">{c.label}</span>
                                      </div>
                                      <span className="text-sm font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">{c.date.toLocaleDateString()}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Upcoming booked meetings */}
                            {upcomingCalls.length > 0 && (
                              <div className="mt-4">
                                <div className="flex items-center gap-1.5 text-sm font-semibold text-blue-700 dark:text-blue-300 mb-2">
                                  <span aria-hidden="true">📅</span> Upcoming booked
                                </div>
                                <div className="space-y-2">
                                  {upcomingCalls.slice(0, 4).map((c, i) => (
                                    <div key={i} className="flex items-center justify-between gap-2">
                                      <div className="flex items-center gap-2.5 min-w-0">
                                        {c.photo ? (
                                          <img
                                            src={c.photo}
                                            alt={c.label}
                                            className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                                            onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }}
                                          />
                                        ) : null}
                                        <div
                                          className="w-9 h-9 rounded-full bg-blue-100 dark:bg-blue-900/40 items-center justify-center text-xs font-medium text-blue-700 dark:text-blue-300 flex-shrink-0"
                                          style={{ display: c.photo ? 'none' : 'flex' }}
                                        >
                                          {c.initials || '📅'}
                                        </div>
                                        <span className="text-base text-gray-800 dark:text-gray-100 truncate">{c.label}</span>
                                      </div>
                                      <span className="text-sm font-medium text-blue-600 dark:text-blue-400 whitespace-nowrap">{c.date.toLocaleDateString()}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Next on roadmap — 3 across, stacked */}
                            <div className="mt-4">
                              <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                                <span aria-hidden="true">🗺️</span> Next on roadmap
                              </div>
                              {nextSteps.length === 0 ? (
                                <div className="text-sm text-gray-500 dark:text-gray-400">No upcoming roadmap items</div>
                              ) : (
                                <div className="grid grid-cols-3 gap-2">
                                  {nextSteps.map((s, i) => (
                                    <div
                                      key={i}
                                      className="flex flex-col items-center text-center rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/30 p-2"
                                    >
                                      <span className="text-2xl leading-none">{departmentEmoji(s.department)}</span>
                                      <span className="mt-1 text-xs font-medium text-gray-800 dark:text-gray-100 line-clamp-2">{s.title}</span>
                                      <span className="mt-0.5 text-[10px] text-gray-400 dark:text-gray-500">{s.department}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          ) : sections[currentSection].isTasks ? (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {(internalUsers.length > 0 ? internalUsers : Array.from({ length: 6 }, (_, i) => null)).map((user, idx) => (
                  <div key={user ? user.id : `col-${idx}`} className="bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 flex flex-col">
                    <div className="p-4 border-b border-gray-200 dark:border-gray-600 flex flex-col items-center relative">
                      {user ? (
                        <>
                          {pickProfilePicUrl(user) ? (
                            <img
                              src={pickProfilePicUrl(user)}
                              alt={user.name || 'User'}
                              className="w-12 h-12 rounded-full object-cover mb-2"
                              onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }}
                            />
                          ) : null}
                          {pickProfilePicUrl(user) ? (
                            <div
                              className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-600 items-center justify-center text-sm font-medium text-gray-700 dark:text-gray-200 mb-2"
                              style={{ display: 'none' }}
                            >
                              {(user.initials || (user.name || 'U').slice(0, 2)).toUpperCase()}
                            </div>
                          ) : (
                            <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                              {(user.initials || (user.name || 'U').slice(0, 2)).toUpperCase()}
                            </div>
                          )}
                          <div className="text-sm font-medium text-gray-800 dark:text-gray-100 text-center truncate max-w-[140px]">
                            {user.name || 'User'}
                          </div>
                          {(() => {
                            const allTasks = (tasksByUser[user.id] || [])
                              .slice()
                              .sort((a, b) => {
                                const da = getDueDateObj(a.dueDate);
                                const db = getDueDateObj(b.dueDate);
                                if (!da && !db) return 0;
                                if (!da) return 1;
                                if (!db) return -1;
                                return da - db;
                              });
                            const totalPages = Math.max(1, Math.ceil(allTasks.length / 3));
                            if (totalPages <= 1) return null;
                            const page = Math.min(taskPageByUser[user.id] || 0, totalPages - 1);
                            const canPrev = page > 0;
                            const canNext = page < totalPages - 1;
                            return (
                              <div className="absolute right-2 top-2 flex items-center gap-1 task-page-dropdown">
                                <button
                                  type="button"
                                  onClick={() => setTaskPageByUser(prev => ({ ...prev, [user.id]: Math.max(0, page - 1) }))}
                                  disabled={!canPrev}
                                  className={`p-1 rounded bg-white/60 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-600 ${!canPrev ? 'opacity-40 cursor-not-allowed' : 'hover:bg-white dark:hover:bg-gray-700'}`}
                                  title="Previous"
                                >
                                  <span className="block w-4 text-xs text-gray-600 dark:text-gray-300" aria-hidden="true">◀</span>
                                </button>
                                <div className="relative">
                                  <button
                                    type="button"
                                    onClick={() => setTaskPageDropdownOpen(taskPageDropdownOpen === user.id ? null : user.id)}
                                    className="text-xs text-gray-600 dark:text-gray-300 px-2 py-1 rounded bg-white/60 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-600 hover:bg-white dark:hover:bg-gray-700 transition-colors"
                                    title="Select page"
                                  >
                                    {page + 1}/{totalPages}
                                  </button>
                                  {taskPageDropdownOpen === user.id && (
                                    <div className="absolute right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto min-w-[60px]">
                                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                                        <button
                                          key={pageNum}
                                          type="button"
                                          onClick={() => {
                                            setTaskPageByUser(prev => ({ ...prev, [user.id]: pageNum - 1 }));
                                            setTaskPageDropdownOpen(null);
                                          }}
                                          className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                                            pageNum === page + 1 
                                              ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 font-medium' 
                                              : 'text-gray-700 dark:text-gray-300'
                                          }`}
                                        >
                                          {pageNum}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setTaskPageByUser(prev => ({ ...prev, [user.id]: Math.min(totalPages - 1, page + 1) }))}
                                  disabled={!canNext}
                                  className={`p-1 rounded bg-white/60 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-600 ${!canNext ? 'opacity-40 cursor-not-allowed' : 'hover:bg-white dark:hover:bg-gray-700'}`}
                                  title="Next"
                                >
                                  <span className="block w-4 text-xs text-gray-600 dark:text-gray-300" aria-hidden="true">▶</span>
                                </button>
                              </div>
                            );
                          })()}
                        </>
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-600" />
                      )}
                    </div>
                    <div className="p-3 space-y-2 overflow-y-auto" style={{ maxHeight: '360px' }}>
                      {(user ? (tasksByUser[user.id] || []) : []).length > 0 ? (
                        (() => {
                          const sorted = ([...(tasksByUser[user.id] || [])]
                            .sort((a, b) => {
                              const da = getDueDateObj(a.dueDate);
                              const db = getDueDateObj(b.dueDate);
                              if (!da && !db) return 0;
                              if (!da) return 1; // no date last
                              if (!db) return -1;
                              return da - db; // earliest first
                            })
                          );
                          const page = Math.min(taskPageByUser[user.id] || 0, Math.max(0, Math.ceil(sorted.length / 3) - 1));
                          const start = page * 3;
                          const visible = sorted.slice(start, start + 3);
                          return visible.map((task) => {
                          const overdue = isOverdue(task.dueDate);
                          return (
                            <div
                              key={task.id}
                              className={`rounded-md p-3 border bg-white dark:bg-gray-800 ${
                                overdue
                                  ? 'border-red-300 dark:border-red-700 bg-red-50/70 dark:bg-red-900/10'
                                  : 'border-gray-200 dark:border-gray-600'
                              }`}
                            >
                              <div className={`text-sm font-semibold truncate ${overdue ? 'text-red-700 dark:text-red-300' : 'text-gray-800 dark:text-gray-100'}`}>
                                {task.title || 'Untitled task'}
                              </div>
                              <div className={`mt-1 text-xs ${overdue ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
                                {formatTaskDueDate(task.dueDate)}
                              </div>
                            </div>
                          );
                          });
                        })()
                      ) : (
                        <div className="text-xs text-gray-500 dark:text-gray-400 text-center py-6">No tasks</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : sections[currentSection].isAdditionalItems ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Questions */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                <div className="bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 rounded-t-lg p-4">
                  <h3 className="text-red-700 dark:text-red-300 font-semibold text-base text-center">Questions</h3>
                </div>
                <div className="p-4 space-y-3">
                  {questions.length === 0 ? (
                    <p className="text-sm text-gray-600 dark:text-gray-300">No questions yet</p>
                  ) : (
                    questions.map((q, idx) => (
                      <div key={idx} className="rounded-lg p-4 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800">
                        <p className="text-sm text-gray-700 dark:text-gray-200 break-words">{q}</p>
                      </div>
                    ))
                  )}
                  <div className="mt-3 flex gap-2">
                    <input
                      type="text"
                      value={newQuestion}
                      onChange={(e) => setNewQuestion(e.target.value)}
                      placeholder="Add a question..."
                      className="flex-1 p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                    />
                    <button
                      onClick={() => {
                        const text = newQuestion.trim();
                        if (!text) return;
                        setQuestions((prev) => [text, ...prev]);
                        setNewQuestion('');
                      }}
                      className="px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>

              {/* Comments */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                <div className="bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 rounded-t-lg p-4">
                  <h3 className="text-red-700 dark:text-red-300 font-semibold text-base text-center">Comments</h3>
                </div>
                <div className="p-4 space-y-3">
                  {comments.length === 0 ? (
                    <p className="text-sm text-gray-600 dark:text-gray-300">No comments yet</p>
                  ) : (
                    comments.map((c, idx) => (
                      <div key={idx} className="rounded-lg p-4 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800">
                        <p className="text-sm text-gray-700 dark:text-gray-200 break-words">{c}</p>
                      </div>
                    ))
                  )}
                  <div className="mt-3 flex gap-2">
                    <input
                      type="text"
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Add a comment..."
                      className="flex-1 p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                    />
                    <button
                      onClick={() => {
                        const text = newComment.trim();
                        if (!text) return;
                        setComments((prev) => [text, ...prev]);
                        setNewComment('');
                      }}
                      className="px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default MeetingMode; 